/**
 * Снимок и запись экрана мобильного приложения без окна выбора источника:
 * рисуется текущая оболочка, прокрутка вложенных списков сохраняется.
 */
import type { Context, Options } from "modern-screenshot"
import { MAX_RECORDING_SEC, pickMimeType, screenBitrate, type RecorderHandle, type RecordingResult } from "./recorder"
import { fixWebmDuration } from "./webm-duration"
import { drawInteractionMarks, trackInteractions, type InteractionTracker } from "./interaction-marks"

const SHELL = "[data-mobile-shell]"
/** Кадр записи — в пикселях экрана (до 2×), но не больше 1920 по длинной стороне: иначе кадр не успевает собраться */
const RECORD_MAX_SCALE = 2
const RECORD_MAX_SIDE = 1920
const FRAME_INTERVAL_MS = 250

function shell(): HTMLElement {
  const node = document.querySelector(SHELL)
  if (!(node instanceof HTMLElement)) throw new Error("Не удалось найти экран приложения")
  return node
}

function shotOptions(node: HTMLElement, scale: number): Options {
  return {
    scale,
    width: node.clientWidth,
    height: node.clientHeight,
    backgroundColor: getComputedStyle(node).backgroundColor || "#ffffff",
    features: { restoreScrollPosition: true },
    timeout: 8000,
    filter: (el) => {
      if (!(el instanceof Element)) return true
      if (el.closest("[data-capture-ignore]")) return false
      if (el instanceof HTMLElement && el.style.display === "none") return false
      return true
    },
  }
}

function stamp() {
  return new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")
}

export async function captureViewportPng(): Promise<File> {
  const { domToBlob } = await import("modern-screenshot")
  const node = shell()
  const scale = Math.min(2, window.devicePixelRatio || 1)
  const blob = await domToBlob(node, { ...shotOptions(node, scale), type: "image/png" })
  return new File([blob], `screenshot-${stamp()}.png`, { type: "image/png" })
}

function recordScale(w: number, h: number): number {
  return Math.max(1, Math.min(RECORD_MAX_SCALE, window.devicePixelRatio || 1, RECORD_MAX_SIDE / Math.max(w, h, 1)))
}

export async function startPageRecording(opts: {
  mic: MediaStream | null
  micError: string | null
  onDone: (r: RecordingResult) => void
  onError: (message: string) => void
}): Promise<RecorderHandle> {
  if (typeof MediaRecorder === "undefined") throw new Error("Браузер не умеет записывать видео")
  const node = shell()
  const scale = recordScale(node.clientWidth, node.clientHeight)
  const canvas = document.createElement("canvas")
  // H.264 в Safari требует чётные стороны кадра
  canvas.width = Math.max(2, Math.round(node.clientWidth * scale) & ~1)
  canvas.height = Math.max(2, Math.round(node.clientHeight * scale) & ~1)
  const ctx = canvas.getContext("2d")
  const capture = canvas.captureStream?.bind(canvas)
  if (!ctx || !capture) throw new Error("Браузер не умеет записывать экран приложения")
  const stream = capture(0)
  const video = stream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack | undefined
  if (!video) throw new Error("Браузер не умеет записывать экран приложения")

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = "high"
  const shot = await import("modern-screenshot")
  let stopped = false
  let timer: ReturnType<typeof setTimeout> | null = null
  // Один контекст на всю запись: шрифты и картинки встраиваются один раз, кадры собираются в разы быстрее
  let context: Context<HTMLElement> | null = null
  let contextKey = ""
  const ensureContext = async () => {
    const node = shell()
    const key = `${node.clientWidth}x${node.clientHeight}`
    if (context && contextKey === key && context.node === node) return context
    if (context) shot.destroyContext(context)
    contextKey = key
    context = await shot.createContext(node, shotOptions(node, canvas.width / Math.max(1, node.clientWidth)))
    return context
  }

  let tracker: InteractionTracker | null = null
  const frame = async () => {
    const current = await ensureContext()
    const frameCanvas = await shot.domToCanvas(current)
    if (stopped) return
    ctx.drawImage(frameCanvas, 0, 0, canvas.width, canvas.height)
    if (tracker) {
      const rect = current.node.getBoundingClientRect()
      drawInteractionMarks(ctx, tracker.marks(), canvas.width / Math.max(1, current.node.clientWidth), { x: -rect.left, y: -rect.top })
    }
    video.requestFrame()
  }

  try {
    await frame()
  } catch {
    /* первый кадр может не собраться — запись всё равно начнётся со следующего */
  }

  const loop = () => {
    if (stopped) return
    // Пока плашку тащат, не снимаем кадр: domToCanvas на весь экран как раз и даёт рывки
    if (document.documentElement.hasAttribute("data-capture-hold")) {
      video.requestFrame()
      timer = setTimeout(loop, 80)
      return
    }
    const started = Date.now()
    void frame()
      .catch(() => {})
      .finally(() => {
        if (stopped) return
        timer = setTimeout(loop, Math.max(0, FRAME_INTERVAL_MS - (Date.now() - started)))
      })
  }

  const micTracks = opts.mic?.getAudioTracks() ?? []
  for (const t of micTracks) stream.addTrack(t)

  const mime = pickMimeType()
  const recorder = new MediaRecorder(stream, {
    ...(mime ? { mimeType: mime } : {}),
    videoBitsPerSecond: screenBitrate(canvas.width, canvas.height),
    audioBitsPerSecond: 64_000,
  })
  const chunks: Blob[] = []
  const startedAt = Date.now()
  let pausedMs = 0
  let pausedAt: number | null = null
  let cancelled = false
  let finished = false
  let limit: ReturnType<typeof setTimeout> | undefined

  const release = () => {
    stopped = true
    tracker?.stop()
    tracker = null
    if (timer) clearTimeout(timer)
    video.stop()
    for (const t of micTracks) t.stop()
    if (limit) clearTimeout(limit)
    if (context) shot.destroyContext(context)
    context = null
  }

  recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data)
  recorder.onerror = () => {
    release()
    opts.onError("Запись прервалась")
  }
  recorder.onstop = () => {
    release()
    if (cancelled || finished) return
    finished = true
    const type = recorder.mimeType || mime || "video/webm"
    const ext = type.includes("mp4") ? "mp4" : "webm"
    const raw = new Blob(chunks, { type: type.split(";")[0] })
    const durationMs = Date.now() - startedAt - pausedMs - (pausedAt ? Date.now() - pausedAt : 0)
    void (ext === "webm" ? fixWebmDuration(raw, durationMs) : Promise.resolve(raw)).then((blob) =>
      opts.onDone({
        file: new File([blob], `screen-recording-${stamp()}.${ext}`, { type: raw.type }),
        durationSec: Math.round(durationMs / 1000),
        hasMic: micTracks.length > 0,
        hasSystemAudio: false,
      }),
    )
  }

  limit = setTimeout(() => recorder.state !== "inactive" && recorder.stop(), MAX_RECORDING_SEC * 1000)
  try {
    recorder.start(1000)
  } catch (e) {
    release()
    throw e
  }
  tracker = trackInteractions()
  timer = setTimeout(loop, FRAME_INTERVAL_MS)

  return {
    stop: () => recorder.state !== "inactive" && recorder.stop(),
    cancel: () => {
      cancelled = true
      if (recorder.state !== "inactive") recorder.stop()
      else release()
    },
    pause: () => {
      if (recorder.state !== "recording") return
      recorder.pause()
      pausedAt = Date.now()
    },
    resume: () => {
      if (recorder.state !== "paused") return
      recorder.resume()
      if (pausedAt) pausedMs += Date.now() - pausedAt
      pausedAt = null
    },
    get paused() {
      return recorder.state === "paused"
    },
    startedAt,
    hasMic: micTracks.length > 0,
    hasSystemAudio: false,
    micError: opts.micError,
    mode: "dom",
    displaySurface: null,
  }
}
